import React, { useState } from 'react';
import { Typography, Button, Container, Box, useTheme, Avatar, Popover } from '@mui/material';

const Hero = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState('');
  
  const handlePopoverOpen = (event, qrcode) => {
    setAnchorEl(event.currentTarget);
    setPopoverContent(qrcode);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  
  return (
    <Box 
      sx={{ 
        background: 'linear-gradient(180deg, #FAF5EB 0%, #ffffff 100%)',
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        pt: 8
      }}
      className="py-16 sm:py-24"
    >
      {/* 背景装饰 */}
      <Box 
        className="absolute inset-0 opacity-30"
        sx={{
          background: 'radial-gradient(circle at 30% 20%, rgba(77, 124, 111, 0.1) 0%, rgba(0, 0, 0, 0) 70%), radial-gradient(circle at 70% 65%, rgba(77, 124, 111, 0.08) 0%, rgba(0, 0, 0, 0) 70%)'
        }}
      />
      
      <Container maxWidth="lg">
        <Box className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Box className="md:col-span-6 z-10">
            <Typography 
              variant="h1" 
              className="text-4xl sm:text-5xl md:text-6xl"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 700,
                lineHeight: 1.1,
                mb: 3
              }}
            >
              地鼠
            </Typography>
            
            <Typography 
              variant="h2" 
              className="text-2xl sm:text-3xl"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                mb: 3,
                lineHeight: 1.2
              }}
            >
              独立开发者
            </Typography>
            
            <Typography 
              variant="body1" 
              className="max-w-xl"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '1.125rem',
                mb: 5,
                lineHeight: 1.6
              }}
            >
              第一代 AI 驱动的全栈工程师。
            </Typography>
            
            <Box className="flex flex-wrap gap-4">
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                href="#experience"
                sx={{ 
                  py: 1.5,
                  px: 4,
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                了解更多
              </Button>
              
              <Button 
                variant="outlined" 
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={(e) => handlePopoverOpen(e, "/images/wechat-qrcode.jpg")}
                onMouseLeave={handlePopoverClose}
                sx={{ 
                  color: theme.palette.text.primary,
                  borderColor: 'rgba(0,0,0,0.15)',
                  '&:hover': { 
                    borderColor: 'rgba(0,0,0,0.3)',
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  },
                  py: 1.5,
                  px: 4,
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                联系我
              </Button>
            </Box>
          </Box>
          
          <Box className="hidden md:flex md:col-span-6 justify-center relative">
            {/* 主要图形 */}
            <Box 
              className="relative z-10 flex items-center justify-center"
              sx={{ width: '100%', height: '100%' }}
            >
              <Box 
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {/* 头像背景装饰 - 来自About组件 */}
                <Box 
                  sx={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(77, 124, 111, 0.08) 0%, rgba(77, 124, 111, 0.02) 100%)',
                    zIndex: 0,
                  }}
                />
                
                <Box
                  sx={{
                    position: 'absolute',
                    width: '260px',
                    height: '260px',
                    borderRadius: '50%',
                    border: '1px dashed rgba(77, 124, 111, 0.2)',
                    zIndex: 0,
                    animation: 'rotate 30s linear infinite',
                    '@keyframes rotate': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(360deg)',
                      },
                    },
                  }}
                />
                
                <Avatar 
                  src="/images/dishu.png" 
                  alt="地鼠的照片"
                  sx={{ 
                    width: 220, 
                    height: 220,
                    border: `6px solid ${theme.palette.background.default}`,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                    position: 'relative',
                    zIndex: 2,
                  }}
                />
                
                {/* 装饰元素 - 来自About组件 */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: '60px',
                    height: '60px',
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #4D7C6F 0%, #7AA095 100%)',
                    bottom: '0',
                    right: '50px',
                    zIndex: 1,
                    transform: 'rotate(-15deg)',
                    boxShadow: '0 10px 20px rgba(77, 124, 111, 0.2)',
                  }}
                />
                
                <Box
                  sx={{
                    position: 'absolute',
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #4D7C6F 0%, #7AA095 100%)',
                    top: '20px',
                    right: '80px',
                    zIndex: 1,
                    transform: 'rotate(30deg)',
                    boxShadow: '0 6px 12px rgba(77, 124, 111, 0.2)',
                  }}
                />
              </Box>
            </Box>
            
            {/* 装饰线条 */}
            <Box 
              className="absolute"
              sx={{
                width: '60%',
                height: '60%',
                border: '2px solid rgba(77, 124, 111, 0.08)',
                borderRadius: '40%',
                top: '20%',
                left: '20%',
                animation: 'rotate 20s linear infinite',
                '@keyframes rotate': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
              }}
            />
          </Box>
        </Box>
        
        <Popover
          id="mouse-over-popover"
          sx={{
            pointerEvents: 'none',
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <Box sx={{ p: 1 }}>
            <img src={popoverContent} alt="微信公众号二维码" style={{ width: 150, height: 150 }} />
          </Box>
        </Popover>
      </Container>
    </Box>
  );
};

export default Hero; 